import React, { useState, useCallback, useContext, memo } from 'react';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core';

import { Document, Rail } from 'components';
import { DocumentValuesContext } from 'contexts';
import { useRailNoteItems } from 'hooks';

/**
 * This component surrounds a document with rails and control bars.
 *
 * It handles
 * - conditional display of the control bars based on the scroll position of the content.
 * - informing the document about changes to the size of the rail content so that it adjusts its margin/padding accordingly.
 *
 * The parent component is responsible for determining what components are used as control bars, as well as the content that appears in the rails.
 */

const useStyles = makeStyles((theme) => {
  return {
    root: {
      position: 'relative',
      height: '100%',
      width: '100%',
      overflow: 'auto',
    },
    container: {
      display: 'flex',
      overflow: 'visible',
      zIndex: '-99999',
      position: 'absolute',
    },
    activeContainer: {
      height: '100%',
      zIndex: 'auto !important',
      position: 'static',
      margin: '0 auto',
      width: ({ isFixedWidth }) => {
        if (isFixedWidth) {
          return 'fit-content';
        }
        return 'auto';
      },
    },
    iframe: {
      flexGrow: 1,
    },
    rail: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'row',
    },
    leftRail: {
      width: '44px',
      minWidth: '44px',
      justifyContent: 'flex-end',
    },
    rightRail: {
      minWidth: '272px',
      [theme.breakpoints.down('sm')]: {
        width: '44px !important',
        minWidth: '44px !important',
      },
      justifyContent: 'flex-start',
    },
    railNoItems: {
      position: 'relative',
      visibility: 'hidden',
      minWidth: '0px !important',
    },
  };
});

function DocumentFraming({
  bookLocation,
  isActive,
  startTTSAfterFitToScreen,
  ...props
}) {
  const { frame, isReadyToDisplay: isDocumentReadyToDisplay } = useContext(
    DocumentValuesContext,
  );

  const isFixedWidth = bookLocation.document?.isFixedWidth;
  const classes = useStyles({ isFixedWidth });
  const [lastUserContentRerender, setLastUserContentRerender] = useState(null);

  // Right side
  const railNoteItems = useRailNoteItems();

  const onDocumentRenderUserContent = useCallback(() => {
    setLastUserContentRerender(new Date().getTime());
    startTTSAfterFitToScreen();
  }, [startTTSAfterFitToScreen]);

  return (
    <div aria-hidden={!isActive} className={isActive ? classes.root : null}>
      <div
        className={clsx(
          classes.container,
          isActive ? classes.activeContainer : null,
        )}
      >
        <Document
          className={clsx(classes.iframe)}
          {...props} // important that any intercepted props appear after ...props
          isActive={isActive}
          bookLocation={bookLocation}
          onDocumentRenderUserContent={onDocumentRenderUserContent}
          isFixedWidth={isFixedWidth}
        />
        {isActive && isDocumentReadyToDisplay ? (
          <Rail
            items={railNoteItems}
            frame={frame}
            className={
              railNoteItems.length
                ? clsx(classes.rail, classes.rightRail)
                : classes.railNoItems
            }
            nonce={lastUserContentRerender}
            railItemMinWidth={272}
            railItemPaddingLeft={0}
          />
        ) : null}
      </div>
    </div>
  );
}

DocumentFraming.defaultProps = {
  isActive: true,
  startTTSAfterFitToScreen: (event) => {},
};

export default memo(DocumentFraming);
